<script>
import baseMixin from '@/mixins/baseMixin'
import navMixin from '@/mixins/navMixin'
import locationMixin from '@/mixins/locationMixin'
import MobileNavDrawer from '@/components/layout/mobile-nav-drawer.vue'
import { ClientType } from '@/shared/constants/client/ClientType'
import { mapGetters, mapActions } from 'vuex'
import AzureFeatureFlags from '@/shared/constants/features/AzureFeatureFlags'
import TheClientFilter from './layout/the-client-filter.vue'
import BasePageTitle from './_base-page-title.vue'
import BasePageSubtitle from './_base-page-subtitle.vue'
import ExportCandidates from './candidate/candidate-export-dialog.vue'

const { WorkplaceRegisterExportFlag } = AzureFeatureFlags

const EC_OSHC_CLIENT_TYPES = [
  ClientType.EARLY_CHILDHOOD,
  ClientType.OSHC,
  ClientType.KINDERGARTEN,
]

export default {
  name: 'BasePageHeader',
  components: {
    MobileNavDrawer,
    TheClientFilter,
    BasePageTitle,
    BasePageSubtitle,
    ExportCandidates,
  },
  mixins: [baseMixin, navMixin, locationMixin],
  props: {
    title: {
      type: String,
      default: 'Page Title',
    },
    subtitle: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    hideIcon: {
      type: Boolean,
      default: false,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      candidateList: 'candidate/candidateList',
      selectedClientIds: 'client/getSelectedClients',
      selectedClients: 'client/mapSelectedClientsToClientsInPermissions',
      isFeatureEnabled: 'features/isFeatureEnabled',
    }),
    clientList() {
      return this.$store.getters['auth/getAllClients']
    },
    exportCandidatesFeatureFlagEnabled() {
      return this.isFeatureEnabled(WorkplaceRegisterExportFlag)
    },
    displayIcon() {
      if (this.hideIcon) return ''
      return this.icon || this.getActiveNavItem?.icon || ''
    },
    hasEcOrOshcClientSelected() {
      return this.selectedClients.every((client) =>
        EC_OSHC_CLIENT_TYPES.includes(client.clientTypeInt)
      )
    },
    showExportCandidates() {
      return (
        this.$route.name === 'candidates' &&
        this.candidateList?.length > 0 &&
        this.isAUSLoginOrLocation &&
        this.hasEcOrOshcClientSelected &&
        this.exportCandidatesFeatureFlagEnabled
      )
    },
  },
  mounted() {
    // not async to allow component to render immediately, displaying export btn when flag call resolves
    this.getFeatureFlag(WorkplaceRegisterExportFlag)
  },
  methods: {
    ...mapActions({
      getFeatureFlag: 'features/getFeatureFlag',
    }),
  },
}
</script>

<template>
  <header class="page-header" :class="{ 'px-3': isMobile }">
    <v-sheet
      class="d-flex align-center justify-space-between transparent"
      min-height="42px"
    >
      <!-- Left side: Nav drawer + Icon + Title -->
      <div class="d-flex align-center">
        <MobileNavDrawer />

        <slot name="icon">
          <v-icon
            v-if="displayIcon"
            class="me-3"
            :class="{ 'ml-4': isMobile }"
            size="20"
          >
            {{ displayIcon }}
          </v-icon>
        </slot>

        <div :class="{ 'ml-4': !displayIcon && isMobile }">
          <slot name="title">
            <BasePageTitle>{{ title }}</BasePageTitle>
            <BasePageSubtitle v-if="subtitle">{{ subtitle }}</BasePageSubtitle>
          </slot>
        </div>
      </div>

      <!-- Right side: Actions -->
      <div v-if="!hideActions" class="page-actions">
        <slot name="actions">
          <transition name="fade-up">
            <ExportCandidates
              v-if="showExportCandidates"
              :client-ids="selectedClientIds"
            />
          </transition>
          <TheClientFilter
            v-if="clientList.length > 1"
            ref="clientFilterOption"
            right
            @update="(payload) => $emit('update:client-selection', payload)"
          />
        </slot>
      </div>
    </v-sheet>
  </header>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/vue-transitions.scss';

.page-header {
  width: 100%;
  margin-bottom: 24px;
}

.v-icon {
  width: 20px;
  height: 20px;
  color: $color-neutral-grey-400 !important;
}

.page-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
</style>
